import {
  Checkbox,
  Flex,
  ListDivider,
  MenuItem,
  ListItemText,
  Select,
  Tooltip,
  SelectMenuTooltip,
  InlineLink,
} from '@fullstory/components';
import { useFlags, useSelectableList } from '@fullstory/react-hooks';
import React, { ReactNode, useMemo, useState } from 'react';
import {
  ACROSS_SESSIONS_LABEL,
  SAME_SESSION_LABEL,
  SessionConstraintsLabel,
  TWO_EVENTS_REQUIRED_TEXT,
} from '../util';
import { DisabledEventToggle } from '../DisabledEventToggle';
import { FormattedMessage, useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';

export interface SessionConstraints {
  inSameSession: boolean;
  completeSessions: boolean;
}

export interface SessionConstraintsSelectProps
  extends SessionConstraints,
    Pick<React.ComponentProps<typeof Select>, 'size'> {
  onChange: (c: SessionConstraints) => void;
  displayName: 'Segment' | 'Funnel' | 'Metric' | 'Journey';
  inSameSessionDisabled: boolean;
}

const COMPLETE_SESSIONS_LABEL = intl.formatMessage({
  defaultMessage: 'include online sessions',
  id: 'ayp35u',
});

const acrossSessionsOption = {
  label: ACROSS_SESSIONS_LABEL,
  value: false,
  key: ACROSS_SESSIONS_LABEL,
};
const sameSessionOption = {
  label: SAME_SESSION_LABEL,
  value: true,
  key: SAME_SESSION_LABEL,
};
const completeSessionsOption = {
  label: COMPLETE_SESSIONS_LABEL,
  value: null,
  key: COMPLETE_SESSIONS_LABEL,
};

export const SessionConstraintsSelect = ({
  inSameSession,
  completeSessions,
  onChange,
  inSameSessionDisabled = false,
  size = 'x-small',
}: SessionConstraintsSelectProps) => {
  const flags = useFlags();
  const [open, setOpen] = useState(false);

  const completeSessionsDisabled = !flags.featureFlags().AppCompleteSessionsConstraint;

  const options = useMemo(
    () => [
      acrossSessionsOption,
      sameSessionOption,
      ...(completeSessionsDisabled ? [] : [completeSessionsOption]),
    ],
    [completeSessionsDisabled],
  );

  const { getListItemProps } = useSelectableList(
    options,
    (maybeOption, option) => maybeOption.key === option.key,
    option => {
      if (option.key === ACROSS_SESSIONS_LABEL || option.key === SAME_SESSION_LABEL) {
        setOpen(false);
        onChange({ completeSessions, inSameSession: option.value! });
      } else {
        onChange({ inSameSession, completeSessions: !completeSessions });
      }
    },
    { active: open },
  );

  const label = SessionConstraintsLabel(
    inSameSession,
    completeSessions && !completeSessionsDisabled,
  );

  return inSameSessionDisabled && completeSessionsDisabled ? (
    <DisabledEventToggle label={label} tooltipText={TWO_EVENTS_REQUIRED_TEXT} />
  ) : (
    <Select
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      displayName={label}
      variant="underline-builder"
      size={size}
      testId="session-constraints"
    >
      <Flex column alignItems="stretch">
        <Select.Options>
          <Select.Option
            selected={acrossSessionsOption.value === inSameSession}
            {...getListItemProps(acrossSessionsOption)}
          >
            {acrossSessionsOption.label}
          </Select.Option>
          <Tooltip
            disabled={!inSameSessionDisabled}
            placement="right-start"
            variant="light"
            tooltipContent={TWO_EVENTS_REQUIRED_TEXT}
          >
            <Select.Option
              selected={sameSessionOption.value === inSameSession}
              disabled={inSameSessionDisabled}
              {...getListItemProps(sameSessionOption)}
            >
              {sameSessionOption.label}
            </Select.Option>
          </Tooltip>
          {!completeSessionsDisabled && (
            <>
              <ListDivider />
              <MenuItem {...getListItemProps(completeSessionsOption)}>
                <Checkbox
                  checked={!completeSessions}
                  onChange={() => null} // onChange handled by parent ListItem
                />
                <ListItemText text={completeSessionsOption.label} />
                <SelectMenuTooltip
                  placement="right"
                  variant="light"
                  tooltipContent={CompleteSessionsTooltip(completeSessions ?? false, 'search')}
                />
              </MenuItem>
            </>
          )}
        </Select.Options>
      </Flex>
    </Select>
  );
};

export const CompleteSessionsTooltip = (
  completeSessions: boolean,
  source: 'search' | 'playlist',
): ReactNode => {
  const intl = useIntl();
  let text = '';
  if (source === 'search')
    text = intl.formatMessage(
      {
        defaultMessage:
          '{completeSessions, select, true {Selecting} other {Deselecting}} this option will {completeSessions, select, true {include} other {hide}} activity from currently active sessions.',
        id: 'RlQkoX',
      },
      { completeSessions },
    );
  else {
    text = intl.formatMessage(
      {
        defaultMessage:
          'Clicking this option will {completeSessions, select, true {include} other {hide}} activity from currently active sessions.',
        id: 'UrQqv7',
      },
      { completeSessions },
    );
  }
  return (
    <>
      {text}{' '}
      <InlineLink
        href="https://help.fullstory.com/hc/en-us/articles/4412900055831"
        newTab
        variant="inverse"
      >
        <FormattedMessage defaultMessage="Learn more" id="TdTXXf" />
      </InlineLink>
    </>
  );
};
